import striptags from 'striptags';
import URI from 'urijs';
import { constants, url, resortAction } from '@clubmed/utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
const COMFORT_EXCLUSIVE = 5;
const REGEXP_DYNAMIC_CRUISE = /^CM2C_\d{8}_\d{8}$/;
const REGEXP_SPAN_MATCH = /<span class="match">([^<]*)<\/span>/g;
export const RESORT_PRODUCT_TYPE = 'resort';

function getType(item: SuggestItem): string {
  return item.product_id ? RESORT_PRODUCT_TYPE : item.type || item.section || '';
}

function isItemResort(item: SuggestItem): boolean {
  return getType(item) === RESORT_PRODUCT_TYPE;
}

function getLabel(item: SuggestItem): string {
  const label = striptags(item.value || '', '<span>');
  return isItemResort(item) && item.sibling_id ? `${label} - ${item.season || ''}` : label;
}

function isCruise(item: SuggestItem): boolean {
  return item.type === constants.PRODUCT_TYPES.CRUISE;
}

function isDynamicCruise(item: SuggestItem): boolean {
  return isCruise(item) && REGEXP_DYNAMIC_CRUISE.test(item.product_id || '');
}

function isJoyview(item: SuggestItem): boolean {
  return item.type === constants.PRODUCT_TYPES.JOYVIEW;
}

function isTour(item: SuggestItem): boolean {
  return item.type === constants.PRODUCT_TYPES.TOUR;
}

function isVilla(item: SuggestItem): boolean {
  return item.type === constants.PRODUCT_TYPES.VILLA;
}

function isExclusiveComfort(item: SuggestItem): boolean {
  return Number(item.comfort_level) >= COMFORT_EXCLUSIVE;
}

function isExclusiveCollection(item: SuggestItem): boolean {
  return isVilla(item) || isCruise(item) || isExclusiveComfort(item);
}

function getDescription(item: SuggestItem): string {
  return striptags(item.description || '', '<span>');
}

function getQueryMatches(item: SuggestItem): Array<string> {
  const queryMatches = [];
  const valueMatches = item.value ? item.value.matchAll(REGEXP_SPAN_MATCH) : [];

  for (const valueMatch of valueMatches) {
    queryMatches.push(valueMatch[1].toLowerCase());
  }

  const descriptionMatches = item.description ? item.description.matchAll(REGEXP_SPAN_MATCH) : [];

  for (const descriptionMatch of descriptionMatches) {
    queryMatches.push(descriptionMatch[1].toLowerCase());
  }

  return [...new Set(queryMatches)];
}

function getProductId(item: SuggestItem): string | null | undefined {
  return isItemResort(item) ? item.product_id : null;
}

function getProductSeason(item: SuggestItem): string {
  return resortAction.getSeasonInfos(item.sibling_id ? item.season_id : constants.ALL_YEAR);
}

function getProductUrl(item: SuggestItem): string {
  const productUrl = new URI('/');

  if (isDynamicCruise(item)) {
    productUrl
      .segment('c')
      .segment(url.normalizePath((item.product_id || '').split('_').join('/')));
  } else if (isTour(item)) {
    productUrl.segment('i').segment(url.normalizeSlug(item.title)).segment(getProductSeason(item));
  } else {
    productUrl.segment('r').segment(url.normalizeSlug(item.title)).segment(getProductSeason(item));
  }

  return productUrl.toString();
}

function getLink(item: SuggestItem): string | null | undefined {
  return isItemResort(item) ? getProductUrl(item) : item.url;
}

/**
 * Sort by 
 - Products (pageType: resort, cruise or tour)
 - Facets - destinations, activités etc.
 - destinations Pages (pagetype: destination and destination-travel and URL that started by /d o /v)
 - landing Pages (pageType: landing and URL that started by /l) 
 * @param {*} item
 * @returns {number} The display position
 */
const getDisplayPosition = (item: SuggestItem): number => {
  // Resort in first
  if (isItemResort(item)) {
    return 1;
  }

  if (item.url) {
    const [, pageType] = item.url.split('/');

    // Destination
    if (pageType === 'd' || pageType === 'v') {
      return 3;
    }

    // Landing
    if (pageType === 'l') {
      return 4;
    }
  }

  return 2;
};

type ProductHeadlines = {
  subLabel1: string | null | undefined;
  subLabel2: string | null | undefined;
  productId: string | null | undefined;
  productType: string | null | undefined;
  comfortLevel: string | null | undefined;
  season: string | null | undefined;
};

function getHeadlines(item: SuggestItem): Partial<ProductHeadlines> {
  return isItemResort(item)
    ? {
        subLabel1: Array.isArray(item.destination_country)
          ? item.destination_country.join(', ')
          : item.destination_country,
        subLabel2: item.destination_area,
        productId: item.product_id,
        productType: item.type,
        comfortLevel: item.comfort_level,
        season: Array.isArray(item.season) ? '' : item.season,
      }
    : {};
}

function getIcon(item: SuggestItem) {
  const comfortLevel = item.comfort_level || 1;

  switch (item.type) {
    case constants.PRODUCT_TYPES.CRUISE:
      return ICONS.CRUISE;

    case constants.PRODUCT_TYPES.JOYVIEW:
      return ICONS.TRIDENT_JOYVIEW;

    case constants.PRODUCT_TYPES.TOUR:
      return ICONS.TOUR;

    case constants.PRODUCT_TYPES.RESORT:
    case constants.PRODUCT_TYPES.VILLA:
      return comfortLevel === String(COMFORT_EXCLUSIVE) ? null : ICONS[`TRIDENT${comfortLevel}`];

    default:
      return item.type === 'activity' ? ICONS.ACTIVITIES : ICONS.ADDRESS;
  }
}

function getImage(item: SuggestItem): string | null | undefined {
  return isItemResort(item) ? item.immersive_image : item.backgroundurl || '';
}

function getColor(item: SuggestItem) {
  if (isExclusiveCollection(item)) {
    return COLORS.ULTRAMARINE;
  }

  if (isJoyview(item)) {
    return COLORS.BLUE_WAVE;
  }

  return COLORS.SAFFRON;
}

const getTagsColor = (item: SuggestItem): string => {
  const seasonName = Array.isArray(item.seasons_id) ? '' : item.seasons_id;

  if (seasonName === constants.SEASONS.SUMMER) {
    return COLORS.ORANGE;
  }

  if (isTour(item) || isCruise(item)) {
    return getColor(item);
  }

  return COLORS.SAFFRON;
};

type ResultItem = Partial<ProductHeadlines> & {
  description: string | null | undefined;
  displayPosition: number;
  icon: string | null | undefined;
  id: string;
  image: string | null | undefined;
  isExclusiveCollection: boolean;
  label: string;
  link: string | null | undefined;
  product_id: string | null | undefined;
  queryMatches: Array<string>;
  type: string;
};
export function getResultItem(item: SuggestItem): ResultItem {
  return {
    description: getDescription(item),
    displayPosition: getDisplayPosition(item),
    icon: getIcon(item),
    id: getProductId(item) || getLabel(item),
    image: getImage(item),
    isExclusiveCollection: isExclusiveCollection(item),
    label: getLabel(item),
    link: getLink(item),
    productColor: getColor(item),
    product_id: getProductId(item),
    queryMatches: getQueryMatches(item),
    tagsColor: getTagsColor(item),
    type: getType(item),
    ...getHeadlines(item),
  };
}
